/**
 * A granted price card's prices, read-only (`/rate-cards/:id`, Prices tab, TMMRSL-0075, MM-D-136):
 * set by the platform, each line's cost to the reseller (wholesale, TMMAPI-0155) beside the
 * customer's price (retail). AI has no wholesale figure here: our AI wholesale markup is never sent
 * (MM-D-105), so the AI lines keep their wording. Required figures render as sent, including 0.
 * A nullable figure keeps its empty treatment. No inputs at all: selectable text (FRM-015).
 */
import type { ReactNode } from 'react';
import {
  Box,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { GrantedBandView } from '@/api/generated';
import type { GrantedCard } from '@/api/rateCards';
import { MoneyCell, NumericCell } from '@/components/cells';
import { DefinitionField, DefinitionList } from '@/components/DefinitionList';
import { moneyIn } from '@/components/moneyIn';
import { bpToPercentText, formatCount } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" component="h2" sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      {children}
    </>
  );
}

/**
 * A free first band then one open-ended per-unit band: "Included free: N", then the price of each
 * additional one. `null` for any other layout, which is listed band by band.
 */
function includedThenPerUnit(
  bands: GrantedBandView[],
): { included: number; perUnitMinor: number; wholesalePerUnitMinor: number | undefined } | null {
  if (bands.length !== 2) return null;
  const [free, paid] = bands as [GrantedBandView, GrantedBandView];
  const isFree = free.flatMinor === 0 && free.perUnitMinor === 0;
  if (!isFree || free.to === null) return null;
  if (paid.to !== null || paid.flatMinor !== 0) return null;
  return {
    included: free.to,
    perUnitMinor: paid.perUnitMinor,
    wholesalePerUnitMinor: paid.wholesalePerUnitMinor,
  };
}

function BandTable({
  bands,
  currency,
  label,
}: {
  bands: GrantedBandView[];
  currency: string;
  label: string;
}) {
  const { t } = useTranslation();
  if (bands.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('rateCards.band.empty')}
      </Typography>
    );
  }
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-label={label}>
        <TableHead>
          <TableRow>
            <TableCell scope="col" align="right" sx={{ whiteSpace: 'nowrap' }}>
              {t('rateCards.band.from')}
            </TableCell>
            <TableCell scope="col" align="right" sx={{ whiteSpace: 'nowrap' }}>
              {t('rateCards.band.to')}
            </TableCell>
            <TableCell scope="col" align="right">
              {t('rateCards.band.wholesaleFlat')}
            </TableCell>
            <TableCell scope="col" align="right">
              {t('rateCards.band.flat')}
            </TableCell>
            <TableCell scope="col" align="right">
              {t('rateCards.band.wholesalePerUnit')}
            </TableCell>
            <TableCell scope="col" align="right">
              {t('rateCards.band.perUnit')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {bands.map((band, index) => (
            // Bands have no identity of their own; a row is its position.
            <TableRow key={index}>
              <NumericCell value={band.from} />
              {band.to !== null ? (
                <NumericCell value={band.to} />
              ) : (
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {t('rateCards.band.andUp')}
                </TableCell>
              )}
              <MoneyCell minor={band.wholesaleFlatMinor} currency={currency} />
              <MoneyCell minor={band.flatMinor} currency={currency} />
              <MoneyCell minor={band.wholesalePerUnitMinor} currency={currency} />
              <MoneyCell minor={band.perUnitMinor} currency={currency} />
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

/** The Line · Your cost (wholesale) · Customer price (retail) table head. */
function PriceHead() {
  const { t } = useTranslation();
  return (
    <TableHead>
      <TableRow>
        <TableCell scope="col">{t('rateCards.prices.col.line')}</TableCell>
        <TableCell scope="col" align="right">
          {t('rateCards.prices.col.wholesale')}
        </TableCell>
        <TableCell scope="col" align="right">
          {t('rateCards.prices.col.retail')}
        </TableCell>
      </TableRow>
    </TableHead>
  );
}

/**
 * Guests or bank connections: "Included free", then the wholesale and retail of each additional
 * one; or every band.
 */
function Bands({
  bands,
  currency,
  title,
  perAdditional,
}: {
  bands: GrantedBandView[];
  currency: string;
  title: string;
  perAdditional: string;
}) {
  const { t } = useTranslation();
  const simple = includedThenPerUnit(bands);
  return (
    <Section title={title}>
      {simple ? (
        <>
          <DefinitionList>
            <DefinitionField label={t('rateCards.prices.includedFreeLabel')}>
              {t('rateCards.prices.includedFree', { count: simple.included })}
            </DefinitionField>
          </DefinitionList>
          <Box sx={{ overflowX: 'auto', mt: 1.5 }}>
            <Table size="small" aria-label={title}>
              <PriceHead />
              <TableBody>
                <TableRow>
                  <TableCell component="th" scope="row">
                    {perAdditional}
                  </TableCell>
                  <MoneyCell minor={simple.wholesalePerUnitMinor} currency={currency} />
                  <MoneyCell minor={simple.perUnitMinor} currency={currency} />
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </>
      ) : (
        <BandTable bands={bands} currency={currency} label={title} />
      )}
    </Section>
  );
}

export function RateCardPrices({ card }: { card: GrantedCard }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const dash = t('statements.unavailable');
  const money = (minor: number) => moneyIn(minor, card.currency, locale, dash);
  const hasAnnual = card.annualBaseMinor !== null;

  return (
    <Paper sx={{ p: 2.5 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('rateCards.prices.setBy')}
      </Typography>

      <Section title={t('rateCards.prices.base.title')}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label={t('rateCards.prices.base.title')}>
            <PriceHead />
            <TableBody>
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('rateCards.prices.base.monthly')}
                </TableCell>
                <MoneyCell minor={card.baseWholesaleMinor} currency={card.currency} />
                <MoneyCell minor={card.baseMinor} currency={card.currency} />
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('rateCards.prices.base.annual')}
                </TableCell>
                {hasAnnual ? (
                  <>
                    <MoneyCell minor={card.annualBaseWholesaleMinor} currency={card.currency} />
                    <MoneyCell minor={card.annualBaseMinor} currency={card.currency} />
                  </>
                ) : (
                  <>
                    {/* tmm-api sends a 0 wholesale without an annual base: it is never shown. */}
                    <TableCell />
                    <TableCell align="right">{t('rateCards.field.noAnnual')}</TableCell>
                  </>
                )}
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </Section>

      <Bands
        bands={card.seatBands}
        currency={card.currency}
        title={t('rateCards.prices.guests')}
        perAdditional={t('rateCards.prices.perAdditionalGuest')}
      />
      <Bands
        bands={card.connectedAccountBands}
        currency={card.currency}
        title={t('rateCards.prices.connections')}
        perAdditional={t('rateCards.prices.perAdditionalConnection')}
      />

      <Section title={t('rateCards.prices.ai.title')}>
        <DefinitionList>
          <DefinitionField label={t('rateCards.prices.ai.priceLabel')}>
            {t('rateCards.prices.ai.price')}
          </DefinitionField>
          <DefinitionField label={t('rateCards.prices.ai.markupLabel')}>
            {t('rateCards.prices.ai.markup', {
              percent: bpToPercentText(card.aiRetailMarkupBp, locale),
            })}
          </DefinitionField>
          <DefinitionField label={t('rateCards.prices.ai.creditLabel')}>
            {money(card.aiMonthlyCreditMinor)}
            <Typography variant="body2" component="span" sx={{ display: 'block', color: 'text.secondary' }}>
              {t('rateCards.prices.ai.creditShare', {
                percent: bpToPercentText(card.aiCreditPlatformShareBp, locale),
              })}
            </Typography>
          </DefinitionField>
          <DefinitionField label={t('rateCards.prices.ai.rechargeLabel')}>
            {money(card.aiRechargeAmountMinor)}
          </DefinitionField>
        </DefinitionList>
      </Section>

      <Section title={t('rateCards.prices.processing.title')}>
        <Typography variant="body2">{t('rateCards.prices.processing.body')}</Typography>
      </Section>

      <Section title={t('rateCards.prices.other')}>
        <DefinitionList>
          <DefinitionField label={t('rateCards.field.trialNew')}>
            {formatCount(card.trialDaysNewSubscriber, locale)}
          </DefinitionField>
          <DefinitionField label={t('rateCards.field.trialGuest')}>
            {formatCount(card.trialDaysGuestConversion, locale)}
          </DefinitionField>
          {card.controls ? (
            <DefinitionField label={t('rateCards.field.grace')}>
              {t('rateCards.field.graceValue', { count: card.controls.graceDaysAfterEnd })}
            </DefinitionField>
          ) : null}
        </DefinitionList>
      </Section>
    </Paper>
  );
}
